import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  InputAdornment,
  Button,
  Alert,
  Fade,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import StepperProgress from './shared/StepperProgress';
import FormStep from './shared/FormStep';
import StepNavigation from './shared/StepNavigation';

export interface GiftContractFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (formData: Record<string, any>) => void;
  onPreview: (formData: Record<string, any>) => void;
  editMode?: boolean;
  existingData?: Record<string, any> | null;
}

const stepsTitles = [
  'Kdo daruje?',
  'Kdo přijímá?',
  'Co se daruje?',
  'Předání a formality',
];

const GiftContractForm: React.FC<GiftContractFormProps> = ({
  open,
  onClose,
  onSuccess,
  onPreview,
  editMode = false,
  existingData = null,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [incompleteSteps, setIncompleteSteps] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const defaultValues = useMemo(() => ({
    gift_category: 'penize',
    ...(existingData || {}),
  }), [existingData]);

  const { control, handleSubmit, watch, trigger, getValues, setValue } = useForm<Record<string, any>>({
    mode: 'onChange',
    defaultValues,
    // Zachovat hodnoty napříč kroky (stejně jako GenericContractForm)
    shouldUnregister: false,
  });

  const giftCategory = watch('gift_category');

  // Clear irrelevant groups when category changes
  useEffect(() => {
    if (!giftCategory) return;
    const clear = (names: string[]) => names.forEach((n) => setValue(n, undefined));

    const movable = [
      'movable_description', 'movable_brand', 'movable_model',
      'movable_serial_number', 'movable_registration_plate', 'movable_vin',
    ];
    const realEstate = [
      'real_estate_description', 'real_estate_address', 'cadastral_info',
      'real_estate_parcel_numbers', 'real_estate_cadastral_unit', 'real_estate_lv', 'encumbrances',
    ];
    const money = [
      'money_amount', 'bank_account_iban', 'bank_account_number',
      'bank_account_bank_code', 'variable_symbol', 'specific_symbol', 'constant_symbol',
    ];
    const other = ['gift_description'];

    if (giftCategory === 'penize') { clear(movable); clear(realEstate); clear(other); }
    else if (giftCategory === 'nemovitost') { clear(movable); clear(money); clear(other); }
    else if (giftCategory === 'movitost') { clear(realEstate); clear(money); clear(other); }
    else if (giftCategory === 'ostatni') { clear(movable); clear(realEstate); clear(money); }
  }, [giftCategory, setValue]);

  const canGoNext = async (): Promise<boolean> => {
    // Validate required fields for the current step based on category
    const requiredByStep: Record<number, string[]> = {
      0: ['donor_name', 'donor_address', 'donor_id'],
      1: ['recipient_name', 'recipient_address', 'recipient_id'],
      2: (() => {
        const base: string[] = ['gift_category'];
        if (giftCategory === 'penize') return base.concat(['money_amount']);
        if (giftCategory === 'nemovitost') return base.concat(['real_estate_description']);
        if (giftCategory === 'movitost') return base.concat(['movable_description']);
        if (giftCategory === 'ostatni') return base.concat(['gift_description']);
        return base;
      })(),
      3: [],
    };
    const fields = requiredByStep[currentStep] || [];
    if (fields.length === 0) return true;
    return trigger(fields as any);
  };

  useEffect(() => {
    // Track incomplete steps (simple heuristic)
    const values = getValues();
    const newIncomplete: number[] = [];
    if (!values.donor_name || !values.donor_address || !values.donor_id) newIncomplete.push(0);
    if (!values.recipient_name || !values.recipient_address || !values.recipient_id) newIncomplete.push(1);
    if (!values.gift_category) newIncomplete.push(2);
    setIncompleteSteps(newIncomplete);
  }, [watch(), getValues]);

  const onNext = async () => {
    const ok = await canGoNext();
    if (!ok) return;
    if (!completedSteps.includes(currentStep)) setCompletedSteps([...completedSteps, currentStep]);
    if (currentStep < stepsTitles.length - 1) setCurrentStep(currentStep + 1);
    else handleSubmitFinal();
  };

  const handleSubmitFinal = async () => {
    try {
      setLoading(true);
      const data = getValues();
      // Sanitizace: odstraň prázdné hodnoty u čísel a převeď čísla na Number
      const toNumber = (v: any) => (v === '' || v === null || v === undefined ? undefined : Number(v));
      const cleaned: Record<string, any> = { ...data };
      // Převod čísel
      if ('money_amount' in cleaned) cleaned.money_amount = toNumber(cleaned.money_amount);
      if ('gift_value' in cleaned) cleaned.gift_value = toNumber(cleaned.gift_value);
      // Odstranění klíčů s undefined (aby se neposílaly a nepadaly validace)
      Object.keys(cleaned).forEach((k) => {
        if (cleaned[k] === undefined) delete cleaned[k];
      });
      onSuccess(cleaned);
    } catch (e: any) {
      setError(e?.message || 'Odeslání formuláře selhalo');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = (index: number) => {
    switch (index) {
      case 0:
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Controller name="donor_name" control={control} rules={{ required: 'Jméno dárce je povinné' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Jméno a příjmení / Název firmy" autoComplete="section-donor name" error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
              )} />
            <Controller name="donor_id" control={control} rules={{ required: 'RČ/IČO je povinné' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Rodné číslo / IČO" autoComplete="section-donor off" error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
              )} />
            <Controller name="donor_address" control={control} rules={{ required: 'Adresa je povinná' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Adresa" multiline rows={2} autoComplete="section-donor address-line1" error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
              )} />
            <Controller name="donor_email" control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label="E‑mail (volitelné)" type="email" autoComplete="section-donor email" error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
              )} />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Controller name="recipient_name" control={control} rules={{ required: 'Jméno obdarovaného je povinné' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Jméno a příjmení / Název firmy" autoComplete="section-recipient name" error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
              )} />
            <Controller name="recipient_id" control={control} rules={{ required: 'RČ/IČO je povinné' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Rodné číslo / IČO" autoComplete="section-recipient off" error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
              )} />
            <Controller name="recipient_address" control={control} rules={{ required: 'Adresa je povinná' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Adresa" multiline rows={2} autoComplete="section-recipient address-line1" error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
              )} />
            <Controller name="recipient_email" control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label="E‑mail (volitelné)" type="email" autoComplete="section-recipient email" error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
              )} />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: 2 }}>
            <Box sx={{ gridColumn: 'span 12' }}>
              <FormControl fullWidth>
                <InputLabel>Kategorie</InputLabel>
                <Controller name="gift_category" control={control} rules={{ required: 'Zvolte kategorii daru' }}
                  render={({ field }) => (
                    <Select {...field} label="Kategorie">
                      <MenuItem value="penize">Peníze</MenuItem>
                      <MenuItem value="movitost">Movitá věc</MenuItem>
                      <MenuItem value="nemovitost">Nemovitost</MenuItem>
                      <MenuItem value="ostatni">Ostatní</MenuItem>
                    </Select>
                  )} />
              </FormControl>
            </Box>

            {giftCategory === 'penize' && (
              <>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Controller name="money_amount" control={control} rules={{ required: 'Zadejte darovanou částku' }}
                    render={({ field, fieldState }) => (
                      <TextField {...field} type="number" label="Darovaná částka" error={!!fieldState.error} helperText={fieldState.error?.message}
                        fullWidth InputProps={{ endAdornment: <InputAdornment position="end">Kč</InputAdornment> }} />
                    )} />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Controller name="bank_account_iban" control={control}
                    render={({ field }) => (
                      <TextField {...field} label="IBAN (volitelné)" fullWidth />
                    )} />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Controller name="bank_account_number" control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Číslo účtu (volitelné)" fullWidth />
                    )} />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Controller name="bank_account_bank_code" control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Kód banky (volitelné)" fullWidth />
                    )} />
                </Box>
              </>
            )}

            {giftCategory === 'nemovitost' && (
              <>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <Controller name="real_estate_description" control={control} rules={{ required: 'Popis nemovitosti je povinný' }}
                    render={({ field, fieldState }) => (
                      <TextField {...field} label="Popis nemovitosti" multiline rows={3} error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
                    )} />
                </Box>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <Controller name="real_estate_address" control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Adresa nemovitosti (volitelné)" multiline rows={2} fullWidth />
                    )} />
                </Box>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <Controller name="cadastral_info" control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Katastrální údaje (parcela/číslo LV, k.ú.)" fullWidth />
                    )} />
                </Box>
              </>
            )}

            {giftCategory === 'movitost' && (
              <>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <Controller name="movable_description" control={control} rules={{ required: 'Popis movité věci je povinný' }}
                    render={({ field, fieldState }) => (
                      <TextField {...field} label="Popis movité věci" multiline rows={3} error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
                    )} />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Controller name="movable_brand" control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Značka (volitelné)" fullWidth />
                    )} />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Controller name="movable_model" control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Model (volitelné)" fullWidth />
                    )} />
                </Box>
              </>
            )}

            {giftCategory === 'ostatni' && (
              <Box sx={{ gridColumn: 'span 12' }}>
                <Controller name="gift_description" control={control} rules={{ required: 'Popis daru je povinný' }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} label="Popis daru" multiline rows={3} error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
                  )} />
              </Box>
            )}

            <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <Controller name="gift_value" control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Hodnota daru (Kč, volitelné)" fullWidth />
                )} />
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Controller name="transfer_date" control={control}
              render={({ field }) => (
                <TextField {...field} label="Datum předání (volitelné)" type="date" InputLabelProps={{ shrink: true }} fullWidth />
              )} />
            <Controller name="notary_required" control={control}
              render={({ field }) => (
                <FormControlLabel control={<Switch checked={!!field.value} onChange={(_, v) => field.onChange(v)} />} label="Notářské ověření podpisů" />
              )} />
            <Box sx={{ gridColumn: 'span 12' }}>
              <Controller name="tax_obligations" control={control}
                render={({ field }) => (
                  <TextField {...field} label="Daňové povinnosti (volitelné)" multiline rows={2} fullWidth />
                )} />
            </Box>
            <Box sx={{ gridColumn: 'span 12' }}>
              <Controller name="gift_reason" control={control}
                render={({ field }) => (
                  <TextField {...field} label="Důvod darování (volitelné)" fullWidth />
                )} />
            </Box>
            <Box sx={{ gridColumn: 'span 12' }}>
              <Controller name="special_conditions" control={control}
                render={({ field }) => (
                  <TextField {...field} label="Zvláštní ujednání (volitelné)" multiline rows={2} fullWidth />
                )} />
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  const renderContent = () => (
    <>
      {error && (
        <Fade in={!!error}>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}

      <StepperProgress
        steps={stepsTitles}
        activeStep={currentStep}
        completedSteps={completedSteps}
        incompleteSteps={incompleteSteps}
        color="success"
        onStepClick={(i) => {
          // umožní návrat na dokončené kroky
          if (completedSteps.includes(i) || i < currentStep) setCurrentStep(i);
        }}
      />

      <Box sx={{ mt: 2 }}>
        <form autoComplete="off">
          <FormStep key={currentStep} title={stepsTitles[currentStep]} color={currentStep === 2 ? 'warning' : 'primary'}>
            {renderStep(currentStep)}
          </FormStep>
        </form>
      </Box>

      <Box sx={{ position: 'sticky', bottom: 0, backgroundColor: '#fff', zIndex: 10, mt: 2, pt: 2, pb: 2 }}>
        <StepNavigation
          currentStep={currentStep}
          totalSteps={stepsTitles.length}
          onNext={onNext}
          onBack={() => currentStep > 0 && setCurrentStep(currentStep - 1)}
          canGoNext={(() => {
            const v = getValues();
            if (currentStep === 0) {
              return Boolean(v.donor_name && v.donor_address && v.donor_id);
            }
            if (currentStep === 1) {
              return Boolean(v.recipient_name && v.recipient_address && v.recipient_id);
            }
            if (currentStep === 2) {
              const cat = v.gift_category;
              if (!cat) return false;
              if (cat === 'penize') return Boolean(v.money_amount);
              if (cat === 'nemovitost') return Boolean(v.real_estate_description);
              if (cat === 'movitost') return Boolean(v.movable_description);
              if (cat === 'ostatni') return Boolean(v.gift_description);
              return true;
            }
            return true;
          })()}
          canGoBack={currentStep > 0}
          isLoading={loading}
          color="success"
          showNext
        />
      </Box>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth
      sx={{ '& .MuiDialog-container': { alignItems: 'stretch' } }}
      PaperProps={{ sx: { borderRadius: '20px', minHeight: '98vh', height: '98vh', width: '98vw', maxWidth: '1400px', m: { xs: 1, sm: 2 } } }}>
      <DialogContent sx={{ p: 4, overflow: 'auto', position: 'relative', maxHeight: 'calc(98vh - 64px)' }}>
        <IconButton aria-label="Zavřít" onClick={onClose} size="small" sx={{ position: 'absolute', right: 12, top: 12, color: 'grey.600' }}>
          <CloseIcon />
        </IconButton>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default GiftContractForm;
